import type {
  CacheExchangeOpts,
  Cache as GraphCache,
} from '@urql/exchange-graphcache'
import { RouteWaypointStatus } from '../../gql/graphql'
import { GetOrderDetail } from '../../graphql/orders'
import { GetTimeClockQuery } from '../../graphql/timeclock'

/**
 * urql graphcache configuration powering OFFLINE write support.
 *
 * graphcache only queues a mutation while offline when that mutation has an
 * `optimistic` resolver configured. So every action that must work offline
 * (clock in/out, change task, acknowledge assignment, route progress) needs an
 * optimistic entry here. On reconnect graphcache replays the queued mutations
 * and the server response overwrites the optimistic entities.
 *
 * The time-clock mutations are nested under the `timeClock` Mutation namespace
 * (`timeClock { clockIn { timeClock } }`), so a single `timeClock` resolver
 * serves clockIn/clockOut/changeTask — and it must emit ONLY the executed
 * action's branch (see the resolver comment / CRE-80).
 */

type AnyCache = GraphCache

/** Reads the current normalized TimeClock (if clocked in) from the cache. */
const readCurrentTimeClock = (cache: AnyCache) => {
  // biome-ignore lint/suspicious/noExplicitAny: cached data is fragment-masked at the type level only
  const data = cache.readQuery({ query: GetTimeClockQuery }) as any
  const me = data?.me
  return me && 'timeClock' in me ? me.timeClock : null
}

const nowIso = () => new Date().toISOString()

/** Selected task for previews — name/payable resolved from the cache. */
const optimisticTask = (cache: AnyCache, taskId?: number | null) => {
  if (!taskId) return null
  const entity = { __typename: 'TimeClockTask', id: taskId }
  return {
    __typename: 'TimeClockTask',
    id: taskId,
    name: cache.resolve(entity, 'name') ?? '',
    payable: cache.resolve(entity, 'payable') ?? true,
  }
}

const synthShift = (tempId: number, start: string) => ({
  __typename: 'TimeClockShift',
  id: tempId,
  startTime: start,
  endTime: null,
})

/**
 * Value objects and mutation-response wrappers that have no `id`. Returning
 * `null` tells graphcache to embed them in their parent instead of trying to
 * normalize them (which would log warnings and drop data).
 */
const keys: CacheExchangeOpts['keys'] = {
  TimeClockMutations: () => null,
  TimeClockMutationsClockInResponse: () => null,
  TimeClockMutationsClockOutResponse: () => null,
  TimeClockMutationsChangeTaskResponse: () => null,
  TimeClockMutationsUpdateResponse: () => null,
  Geolocation: () => null,
  Address: () => null,
  AssignmentRole: () => null,
  EquipmentTelematics: () => null,
  TelematicsGPS: () => null,
  TelematicsStats: () => null,
  Customer: () => null,
  // Order.statusDetails is an OrderStatus value object with no id.
  OrderStatus: () => null,
  // Photo mutations are intentionally NOT queueable offline (no optimistic
  // entries): the S3 binary PUT can't ride graphcache, so the photo upload
  // queue owns offline instead. These wrappers just need embedding.
  OrderPhotoMutations: () => null,
  OrderPhotoPresignResponse: () => null,
  OrderSignatureStatus: () => null,
  NotificationMutations: () => null,
  NotificationMutationsTriggerResponse: () => null,
}

const optimistic: CacheExchangeOpts['optimistic'] = {
  // One resolver for clockIn/clockOut/changeTask. Must emit ONLY the executed
  // branch (the updater receives this raw object, CRE-80). Field args are
  // always {} — the input arrives via info.variables. No task = clock-out;
  // task while active = change-task; else clock-in.
  timeClock: (_fieldArgs, cache, info) => {
    const data = (info.variables as { data?: { task?: { id?: number } } }).data
    const current = readCurrentTimeClock(cache)
    const now = nowIso()
    const tempId = Date.now() * -1
    const shift = current?.shift ?? synthShift(tempId, now)
    const isActive = Boolean(current && !current.endTime)

    // The running clock, ended or re-tasked (clock-out / change-task)
    const continuedClock = (endTime: string | null, task: unknown) => ({
      __typename: 'TimeClock',
      id: current?.id ?? tempId,
      startTime: current?.startTime ?? now,
      endTime,
      task,
      shift,
    })

    if (data?.task && isActive) {
      return {
        __typename: 'TimeClockMutations',
        changeTask: {
          __typename: 'TimeClockMutationsChangeTaskResponse',
          timeClock: continuedClock(null, optimisticTask(cache, data.task.id)),
        },
      }
    }

    if (isActive) {
      return {
        __typename: 'TimeClockMutations',
        clockOut: {
          __typename: 'TimeClockMutationsClockOutResponse',
          timeClock: continuedClock(now, current?.task ?? null),
        },
      }
    }

    return {
      __typename: 'TimeClockMutations',
      clockIn: {
        __typename: 'TimeClockMutationsClockInResponse',
        timeClock: {
          __typename: 'TimeClock',
          id: tempId,
          startTime: now,
          endTime: null,
          task: optimisticTask(cache, data?.task?.id),
          shift,
        },
      },
    }
  },

  // biome-ignore lint/suspicious/noExplicitAny: graphcache passes loosely-typed variables
  acknowledgeAssignment: (vars: any) => ({
    __typename: 'Assignment',
    id: vars.where.id,
    acknowledged: true,
    acknowledgedAt: nowIso(),
  }),

  // biome-ignore lint/suspicious/noExplicitAny: graphcache passes loosely-typed variables/cache
  updateRouteWaypoint: (vars: any, cache) => {
    const arriving = vars.data?.actualArrivalTime != null
    const key = cache.keyOfEntity({
      __typename: 'RouteWaypoint',
      id: vars.where.id,
    })
    const currentArrival = key
      ? (cache.resolve(key, 'actualArrivalTimeLocal') as string | null)
      : null
    const currentDeparture = key
      ? (cache.resolve(key, 'actualDepartureTimeLocal') as string | null)
      : null

    return {
      __typename: 'RouteWaypoint',
      id: vars.where.id,
      status: arriving
        ? RouteWaypointStatus.InProgress
        : RouteWaypointStatus.Completed,
      actualArrivalTimeLocal: vars.data?.actualArrivalTime ?? currentArrival,
      actualDepartureTimeLocal:
        vars.data?.actualDepartureTime ?? currentDeparture,
    }
  },

  // Work ticket submit/save (SubmitWorkTicket → updateOrder). Auto-merges into
  // the Order by id; a status change (turn-in) updates the order optimistically.
  // biome-ignore lint/suspicious/noExplicitAny: graphcache passes loosely-typed variables/cache
  updateOrder: (vars: any, cache) => {
    const id = vars.where?.id
    const orderKey = cache.keyOfEntity({ __typename: 'Order', id })
    const newStatus = vars.data?.status as string | undefined
    const status =
      newStatus ??
      (orderKey ? (cache.resolve(orderKey, 'status') as string | null) : null)
    // Preserve the current status label on partial saves; synthesize on a
    // status change (the real label arrives on replay).
    const sdKey =
      !newStatus && orderKey
        ? (cache.resolve(orderKey, 'statusDetails') as string | null)
        : null
    return {
      __typename: 'Order',
      id,
      status: status ?? null,
      statusDetails: {
        __typename: 'OrderStatus',
        slug:
          (sdKey ? (cache.resolve(sdKey, 'slug') as string) : null) ??
          status ??
          '',
        name: (sdKey ? (cache.resolve(sdKey, 'name') as string) : null) ?? '',
      },
    }
  },

  // Customer / work-ticket signature (CollectOrderSignature → Signature { id }).
  // A temp negative id is enough to queue the mutation offline; the real
  // signature replaces it on reconnect.
  collectOrderSignature: () => ({
    __typename: 'Signature',
    id: Date.now() * -1,
  }),
}

const updates: CacheExchangeOpts['updates'] = {
  Mutation: {
    // The time-clock mutations return a wrapper type, not the `me` query field,
    // so re-link the resulting TimeClock into GetTimeClockQuery. Runs for both
    // the optimistic phase and the real replay (idempotent), so the temp clock
    // is swapped for the server clock on sync.
    // biome-ignore lint/suspicious/noExplicitAny: graphcache result is loosely typed
    timeClock: (result: any, _args, cache) => {
      const tc = result?.timeClock
      const updated =
        tc?.clockIn?.timeClock ??
        tc?.clockOut?.timeClock ??
        tc?.changeTask?.timeClock
      if (!updated) return
      cache.updateQuery({ query: GetTimeClockQuery }, (data) => {
        // biome-ignore lint/suspicious/noExplicitAny: union/interface me field
        const me = (data as any)?.me
        if (!me || !('timeClock' in me)) return data
        return { ...data, me: { ...me, timeClock: updated } }
      })
    },

    // The signature mutation returns only `Signature { id }`, so the order's
    // `terms.accepted` (which gates the Customer Signature button) doesn't move
    // on its own — offline that left the button re-prompting after a collection.
    // Flip it locally here. Only for the CUSTOMER (terms) signature: the
    // work-ticket signature shares this mutation but tags itself with the
    // `workTicket` metadata key and must NOT mark terms as accepted.
    // biome-ignore lint/suspicious/noExplicitAny: graphcache args are loosely typed
    collectOrderSignature: (_result: any, args: any, cache) => {
      const upsert = args?.data?.metadata?.upsert
      const isWorkTicket =
        Array.isArray(upsert) &&
        upsert.some((kv: { key?: string }) => kv?.key === 'workTicket')
      if (isWorkTicket) return
      const orderId = args?.where?.id
      if (orderId == null) return
      cache.updateQuery(
        { query: GetOrderDetail, variables: { orderId } },
        (data) => {
          // biome-ignore lint/suspicious/noExplicitAny: cached order shape
          const order = (data as any)?.order
          if (!order?.terms) return data
          return {
            ...data,
            order: {
              ...order,
              terms: {
                ...order.terms,
                accepted: true,
                needsAcceptance: false,
              },
            },
          }
        },
      )
    },
  },
}

export const graphcacheConfig = { keys, optimistic, updates }
